import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api, {
  storeSession,
  getStoredUser,
  getStoredOrgId,
  getStoredAccessToken,
  getStoredRefreshToken,
  clearSession,
  performTokenRefresh,
  unwrapSingle,
  registerPermissionsChangedHandler,
} from "@/lib/api";

const AuthContext = createContext(null);

export function orgTypeToFrontendRole(orgType) {
  switch (orgType) {
    case "PLATFORM":
      return "super_admin";
    case "POLICE":
      return "police";
    case "AMBULANCE":
      return "ambulance";
    case "FIRE_DEPARTMENT":
      return "fire";
    case "HOSPITAL":
      return "hospital";
    default:
      return null;
  }
}

/**
 * Returns the dashboard home path for a given `org_type`.
 */
export function orgTypeHomePath(orgType) {
  switch (orgType) {
    case "PLATFORM":
      return "/admin";
    case "POLICE":
      return "/police";
    case "AMBULANCE":
      return "/ambulance";
    case "FIRE_DEPARTMENT":
      return "/fire";
    case "HOSPITAL":
      return "/hospital";
    default:
      return "/login";
  }
}

/**
 * Legacy helper — kept for backward compat but prefer orgTypeHomePath.
 */
export function roleHomePath(role) {
  switch (role) {
    case "super_admin":
      return "/admin";
    case "police":
      return "/police";
    case "ambulance":
      return "/ambulance";
    case "fire":
      return "/fire";
    case "hospital":
      return "/hospital";
    default:
      return "/login";
  }
}

function getJwtExpiryDelayMs(token, fallbackSeconds = 600) {
  if (!token || typeof token !== "string") {
    return Math.max((fallbackSeconds - 60) * 1000, 10_000);
  }

  try {
    const payloadBase64 = token.split(".")[1];
    if (!payloadBase64) {
      return Math.max((fallbackSeconds - 60) * 1000, 10_000);
    }

    const normalized = payloadBase64.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=");
    const payload = JSON.parse(atob(padded));
    const expMs = Number(payload?.exp) * 1000;
    if (!Number.isFinite(expMs)) {
      return Math.max((fallbackSeconds - 60) * 1000, 10_000);
    }

    return Math.max(expMs - Date.now() - 60_000, 10_000);
  } catch {
    return Math.max((fallbackSeconds - 60) * 1000, 10_000);
  }
}

function normalizePermissions(list) {
  if (!Array.isArray(list)) return [];
  return [...new Set(list.filter((x) => typeof x === "string" && x.trim().length > 0))];
}

function normalizeMePayload(payload = {}) {
  return {
    user_id: payload.user_id ?? null,
    email: payload.email ?? "",
    name: payload.name ?? "",
    org_id: payload.org_id ?? null,
    org_type: payload.org_type ?? null,
    org_name: payload.org_name ?? "",
    role: payload.role ?? "",
    must_change_password: Boolean(payload.must_change_password),
    aud: payload.aud ?? null,
    permissions: normalizePermissions(payload.permissions),
  };
}

export async function fetchMe() {
  const response = await api.get("/auth/me");
  const body = unwrapSingle(response) || {};
  return normalizeMePayload(body);
}

export function AuthProvider({ children }) {
  const initialUser = getStoredUser();
  const [user, setUser] = useState(() => initialUser);
  const [orgId, setOrgId] = useState(() => getStoredOrgId());
  const [permissions, setPermissions] = useState(() => new Set(normalizePermissions(initialUser?.permissions)));
  const [permissionsStatus, setPermissionsStatus] = useState("idle");
  const refreshTimerRef = useRef(null);

  // Always reflects the latest user value so setTimeout callbacks never close over a stale snapshot.
  // useEffect with [user] runs synchronously after every render where user changed — well before
  // any scheduled timer fires, even the 10-second floor case.
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  // Schedule a silent token refresh 1 minute before the access token expires
  const scheduleRefresh = useCallback((expiresInSeconds, accessToken = getStoredAccessToken()) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);

    const safeExpiresIn = Number.isFinite(Number(expiresInSeconds)) ? Number(expiresInSeconds) : 600;
    const delayMs = getJwtExpiryDelayMs(accessToken, safeExpiresIn);

    refreshTimerRef.current = setTimeout(async () => {
      // Silent abort: if the user already logged out before the timer fired, do nothing.
      if (!getStoredRefreshToken()) return;

      try {
        // Shares the same in-flight Promise as the 401 interceptor — guaranteed
        // no concurrent second refresh call can race with this one.
        const { accessToken: newAccess, refreshToken: newRefresh, expiresIn } = await performTokenRefresh();
        // performTokenRefresh already wrote tokens to localStorage + updated the
        // axios default header. storeSession is called solely to also persist the
        // current user object so the stored session stays consistent.
        storeSession(newAccess, newRefresh, userRef.current);
        setUser(userRef.current);
        setOrgId(userRef.current?.org_id || null);
        scheduleRefresh(expiresIn, newAccess);
      } catch {
        // Refresh failed — force logout
        clearSession();
        setUser(null);
        window.location.href = "/login";
      }
    }, delayMs);
  }, []); // no deps — reads userRef.current at fire time, not the captured value at schedule time

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []);

  const syncMe = useCallback(async () => {
    setPermissionsStatus("loading");
    setPermissions(new Set());

    const me = await fetchMe();

    const mergedUser = {
      ...(getStoredUser() || user || {}),
      ...me,
      frontendRole: orgTypeToFrontendRole(me?.org_type),
      permissions: normalizePermissions(me.permissions),
    };

    const access = getStoredAccessToken();
    const refresh = getStoredRefreshToken();
    if (access && refresh) {
      storeSession(access, refresh, mergedUser);
    }

    setUser(mergedUser);
    setOrgId(mergedUser?.org_id || null);
    setPermissions(new Set(mergedUser.permissions));
    setPermissionsStatus("loaded");

    return mergedUser;
  }, [user]);

  useEffect(() => {
    registerPermissionsChangedHandler(syncMe);
    return () => registerPermissionsChangedHandler(null);
  }, [syncMe]);

  // On mount, if we have a stored user, schedule a refresh
  // (we don't know exact remaining TTL, so use a conservative 10-min guess)
  useEffect(() => {
    const run = async () => {
      if (user && getStoredRefreshToken()) {
        scheduleRefresh(600);
        try {
          await syncMe();
        } catch {
          setPermissionsStatus("error");
        }
      }
    };

    run();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Authenticate with email + password against the real backend.
   * Returns the user object with: user_id, email, name, org_id, org_type, org_name, role
   */
  const login = async (email, password) => {
    const { data: raw } = await api.post("/auth/login", { email, password });
    const data = raw?.data || raw || {};

    const accessToken = data.access_token || data.accessToken || null;
    const refreshTok = data.refresh_token || data.refreshToken || null;
    const expiresIn = data.expires_in || data.expiresIn || 600;
    const userPayload = data.user || data.user_data || data.userData || data;

    const userData = {
      ...userPayload,
      // Add the frontend role for backward compat with DashboardLayout/Sidebar
      frontendRole: orgTypeToFrontendRole(userPayload?.org_type),
      permissions: normalizePermissions(userPayload?.permissions),
    };

    if (!accessToken) throw new Error("Login response missing access token");
    if (!refreshTok) throw new Error("Login response missing refresh token");

    storeSession(accessToken, refreshTok, userData);
    setUser(userData);
    setOrgId(userData?.org_id || null);
    setPermissions(new Set());
    setPermissionsStatus("loading");

    // Schedule token refresh
    scheduleRefresh(expiresIn ?? 600, accessToken);

    try {
      await syncMe();
    } catch {
      setPermissionsStatus("error");
    }

    return userData;
  };

  /**
   * Revoke tokens on the server and clear local session.
   */
  const logout = async () => {
    const refreshToken = getStoredRefreshToken();

    // Fire-and-forget: don't block on server response
    try {
      if (refreshToken) {
        await api.post("/auth/logout", { refresh_token: refreshToken });
      }
    } catch {
      // Logout should never fail from the user's perspective
    }

    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    clearSession();
    setUser(null);
    setOrgId(null);
    setPermissions(new Set());
    setPermissionsStatus("idle");
  };

  /**
   * Self-service password change for the currently logged-in user.
   */
  const updateMyPassword = async (password) => {
    const userId = user?.user_id;
    if (!userId) throw new Error("User session is missing user_id.");

    await api.patch(`/users/${userId}`, { password });

    const updatedUser = {
      ...user,
      must_change_password: false,
    };

    const accessToken = getStoredAccessToken();
    const refreshToken = getStoredRefreshToken();
    if (accessToken && refreshToken) {
      storeSession(accessToken, refreshToken, updatedUser);
    }
    setUser(updatedUser);

    return updatedUser;
  };

  // ── Mock stubs (Signup, OTP, Forgot Password — not backed by API yet) ──
  const signup = async (payload) => {
    return { ...payload, otpRequired: true };
  };

  const verifyOtp = async (code) => {
    if (code !== "123456") throw new Error("Invalid OTP. Use 123456 for demo.");
    return true;
  };

  const can = useCallback(
    (key) => {
      if (permissionsStatus !== "loaded") return false;
      if (!key || typeof key !== "string") return false;
      return permissions.has(key);
    },
    [permissions, permissionsStatus],
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        orgId,
        login,
        signup,
        verifyOtp,
        logout,
        updateMyPassword,
        permissions,
        permissionsStatus,
        permissionsLoaded: permissionsStatus === "loaded",
        can,
        syncMe,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
